/* eslint-disable max-lines */
import { showErrorAlert } from 'actions';
import {
  cleanFacets,
  cleanVariables,
  discardIdentifierChanges,
  fetchPromptBlockTypes,
  promoteIdentifier,
  selectCustomerIdentifier,
  setActiveLanguageMatcherId,
  setCompareModeIdentifier,
  setCustomerGroupIdentifier,
  setPromptCompareMode,
  setSelectedIdentifierName,
  setSelectedIdentifierOptionsConfig,
  upsertIdentifier,
} from 'actions/identifier';
import { fetchPromptTemplates } from 'actions/promptTemplates';
import CollapsableCard from 'components/CollapsableCard';
import LeavePageModal from 'components/LeavePageModal';
import Modal from 'components/Modal';
import SelectRedesign from 'components/Select/SelectRedesign';
import TermsInput from 'components/TermsInput';
import WarningMsg from 'components/WarningMsg';
import { MODAL_BACKGROUND } from 'constants/common';
import { deploymentOptions, kindOptions, sourceOptions } from 'constants/entities';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  getActiveLanguageMatcher,
  getCompareMode,
  getCustomersMissingThresholds,
  getCustomersMissingVariables,
  getIdentifierCustomers,
  getProductionIdentifierRevision,
  getSelectedCustomerIdentifier,
  getSelectedIdentifierRevision,
  getShouldDisableEdit,
  saveIdentifierRequestLoading,
  unsavedChanges,
} from 'selectors/identifier';
import { useSelector } from 'store';
import { languageMatcherNotificationMsgs } from 'utils/userNotificationMsgs';
import CategorizerWorkspaceTable from './CategorizerWorkspaceTable';
import PromptBlockMatcher from './PromptBlockMatcher';
import WorkspaceThresholdManagement from './WorkspaceThresholdManagement';

type LanguageMatcherType = {
  isSaved: boolean;
};

const LLMCategorizer: React.FC<LanguageMatcherType> = ({ isSaved }) => {
  const dispatch = useDispatch();

  const identifierRevision = useSelector(getSelectedIdentifierRevision);
  const prodRevision = useSelector(getProductionIdentifierRevision);
  const unsaved = useSelector(unsavedChanges);
  const isLoadingSaveIdentifier = useSelector(saveIdentifierRequestLoading);
  const compareMode = useSelector(getCompareMode);
  const shouldDisable = useSelector(getShouldDisableEdit);
  const activeLanguageMatcher = useSelector(getActiveLanguageMatcher);
  const selectedCustomer = useSelector(getSelectedCustomerIdentifier);
  const customersMissingThresholds = useSelector(getCustomersMissingThresholds);
  const customersMissingVariables = useSelector(getCustomersMissingVariables);
  const customers = useSelector(getIdentifierCustomers);

  const [isPromoteModalOpen, setIsPromoteModalOpen] = useState(false);
  const [isMissingCategoriesModalOpen, setIsMissingCategoriesModalOpen] = useState(false);
  const [isMissingValuesModalOpen, setIsMissingValuesModalOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    dispatch(setActiveLanguageMatcherId(''));
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchPromptBlockTypes());
    dispatch(fetchPromptTemplates());
  }, [dispatch]);

  if (!identifierRevision?.options_config) {
    return null;
  }

  const {
    categorization_facets: categories,
    content_source: source,
    variables = [],
    model,
    type,
  } = identifierRevision?.options_config as {
    content_source: string;
    model: string;
    type: string;
    categorization_facets: string[];
    variables: string[];
  };
  const { name } = identifierRevision;

  const {
    content_source: prodSource,
    model: prodModel,
    type: prodType,
  } = (prodRevision?.options_config || identifierRevision?.options_config) as {
    content_source: string;
    model: string;
    type: string;
    categorization_facets: string[];
    variables: string[];
  };
  const { name: prodName } = prodRevision || identifierRevision;

  const toggleCategoriesModal = (): void => {
    setIsMissingCategoriesModalOpen(!isMissingCategoriesModalOpen);
  };

  const toggleValuesModal = (): void => {
    setIsMissingValuesModalOpen(!isMissingValuesModalOpen);
  };

  const onSubmit = (): boolean => {
    if (!identifierRevision) return false;

    if (customersMissingThresholds.length > 0 || customersMissingVariables.length > 0) {
      toggleValuesModal();
      return false;
    }

    if (!activeLanguageMatcher) {
      if (!identifierRevision.name) {
        dispatch(showErrorAlert('Prompt Name is missing'));
        return false;
      }
    }

    if (activeLanguageMatcher && !activeLanguageMatcher.name) {
      dispatch(showErrorAlert('Block Name is missing'));
      return false;
    }

    if (activeLanguageMatcher && !activeLanguageMatcher.text) {
      dispatch(showErrorAlert('Pattern is missing'));
      return false;
    }

    dispatch(
      upsertIdentifier({
        ...identifierRevision,
        identifierId: identifierRevision.uuid,
      })
    );
    return true;
  };

  const handleEditName = (e: React.ChangeEvent<HTMLInputElement>): void => {
    dispatch(setSelectedIdentifierName(e.target.value));
  };

  const handleSelectValue = (value: string, property: string): void => {
    const config = {
      ...identifierRevision.options_config,
      // @ts-ignore
      [property]: value,
    };

    dispatch(setSelectedIdentifierOptionsConfig(config));
  };

  const changeCategories = (value: string): void => {
    if (value && !categories.includes(value)) {
      const config = {
        ...identifierRevision.options_config,
        categorization_facets: [...categories, value],
      };
      dispatch(setSelectedIdentifierOptionsConfig(config));
    }
  };

  const deleteCategories = (value: string): void => {
    if (categories.includes(value)) {
      const config = {
        ...identifierRevision.options_config,
        categorization_facets: categories.filter((t) => t !== value),
      };
      dispatch(setSelectedIdentifierOptionsConfig(config));
      dispatch(cleanFacets({ facet: value }));
    }
  };

  const changeVariables = (value: string): void => {
    if (value && !variables.includes(value)) {
      const config = {
        ...identifierRevision.options_config,
        variables: [...variables, value],
      };
      dispatch(setSelectedIdentifierOptionsConfig(config));
    }
  };

  const deleteVariables = (value: string): void => {
    if (variables.includes(value)) {
      const config = {
        ...identifierRevision.options_config,
        variables: variables.filter((t) => t !== value),
      };
      dispatch(setSelectedIdentifierOptionsConfig(config));
      dispatch(cleanVariables({ variable: value }));
    }
  };

  const handleCancel = (): void => {
    dispatch(setActiveLanguageMatcherId(''));
    dispatch(setPromptCompareMode({ isComparing: false, secondaryMatcher: '' }));
    dispatch(selectCustomerIdentifier(''));
  };

  const handleExit = (): void => {
    dispatch(setCompareModeIdentifier(false));
  };

  const toggleModal = (): void => {
    setIsPromoteModalOpen((b) => !b);
  };

  const handlePromote = (): void => {
    if (identifierRevision?.identifier_head_uuid && identifierRevision.identifier_uuid) {
      dispatch(
        promoteIdentifier({
          identifierHead: identifierRevision.identifier_head_uuid,
          identifierId: identifierRevision.identifier_uuid,
        })
      );
    }
    toggleModal();
    dispatch(setCustomerGroupIdentifier('staging'));
    dispatch(setCompareModeIdentifier(false));
  };

  const renderCompareModeButtons = (
    <div className="flex flex-row gap-2">
      <button
        data-testid="save-matchers-button"
        type="button"
        className="button button--secondary h-8 w-22 justify-center text-body"
        onClick={(e): void => {
          handleExit();
          e.stopPropagation();
        }}
      >
        <span className="font-bold">Exit</span>
      </button>
      <button
        data-testid="save-matchers-button"
        type="button"
        className="button button--primary h-8 w-44 justify-center text-body"
        disabled={shouldDisable || unsaved}
        onClick={(e): void => {
          toggleModal();
          e.stopPropagation();
        }}
      >
        <span className="font-bold text-white">Push to Production</span>
      </button>
    </div>
  );

  const renderSaveConfig = (
    <div className="h-8 flex flex-row items-end gap-2">
      {unsaved && (
        <button
          data-testid="save-matchers-button"
          type="button"
          className="button button--secondary h-full w-22 justify-center"
          onClick={(e): void => {
            dispatch(discardIdentifierChanges());
            e.stopPropagation();
          }}
        >
          <span className="font-bold">Discard</span>
        </button>
      )}
      {(activeLanguageMatcher || selectedCustomer) && (
        <button
          data-testid="cancel-matchers-button"
          type="button"
          className="button button--secondary h-full w-22 justify-center text-body"
          onClick={(e): void => {
            handleCancel();
            e.stopPropagation();
          }}
        >
          <span className="font-bold">Cancel</span>
        </button>
      )}
      <button
        id="save-identifier-setup-button"
        onClick={(e): void => {
          onSubmit();
          e.stopPropagation();
        }}
        type="button"
        className="button button--primary h-8"
      >
        Save
      </button>
    </div>
  );

  const renderCustomActions = (): JSX.Element => {
    if (compareMode) {
      return renderCompareModeButtons;
    }
    return renderSaveConfig;
  };

  const renderBody = (): JSX.Element => (
    <>
      <div className="absolute top-5 left-0 right-0 h-40 z-0">{MODAL_BACKGROUND}</div>
      <div className="flex flex-col mt-30 mb-8 gap-4">
        <h2 className="text-heading-1">Push Changes?</h2>
      </div>
    </>
  );

  const renderMissingCategories = (): JSX.Element => (
    <>
      <div className="absolute top-5 left-0 right-0 h-40 z-0">{MODAL_BACKGROUND}</div>

      <div className="flex flex-col mt-30 mb-8 gap-2">
        <h2 className="text-heading-1">Missing category values</h2>
        <p className="text-body">There are missing values in the category field</p>
      </div>
    </>
  );

  const renderMissingValues = (): JSX.Element => (
    <>
      <div className="absolute top-5 left-0 right-0 h-40 z-0">{MODAL_BACKGROUND}</div>

      <div className="flex flex-col mt-30 mb-8 gap-2">
        <h2 className="text-heading-1">Missing value(s)</h2>
        <p className="text-body">
          There are missing values in the following workspaces:{' '}
          {customers
            .filter(
              (customer) =>
                customersMissingThresholds.includes(customer.uuid) ||
                customersMissingVariables.includes(customer.uuid)
            )
            .map((c) => c.name)
            .join(', ')}
        </p>
      </div>
    </>
  );

  const renderContent = (): JSX.Element => {
    if (activeLanguageMatcher) {
      return <PromptBlockMatcher />;
    }
    if (selectedCustomer) {
      return <WorkspaceThresholdManagement isSaved={false} />;
    }

    return (
      <div className="w-full h-full flex flex-col gap-4 pt-4 px-6 pb-10 text-body overflow-hidden">
        {unsaved && (
          <div className="flex flex-row justify-end">
            <WarningMsg
              message={languageMatcherNotificationMsgs.unsavedChanges}
              showLoading={isLoadingSaveIdentifier}
              showWarning={unsaved}
              showSavedMsg={isSaved}
            />
          </div>
        )}
        <div className="h-full flex flex-col gap-4 overflow-hidden">
          <div className="flex flex-row gap-5">
            <label htmlFor="feature" className="flex flex-col gap-1 text-body w-1/2">
              <div className="flex flex-row">
                <span>Prompt name</span>
                <span className="text-litlingo-alert">*</span>
              </div>
              <input
                name="name"
                id="name"
                type="text"
                data-testid="matcher-feature-input"
                value={name?.toString()}
                className={`form-input w-full h-9 py-2 px-4 border border-litlingo-gray-2 rounded-lg text-body focus:outline-none ${
                  prodName !== name && compareMode ? 'bg-litlingo-focus-area-background' : ''
                }`}
                onChange={handleEditName}
                disabled={compareMode || shouldDisable}
                pattern=".*\S.*"
              />
            </label>
          </div>

          <div className="flex flex-row gap-4">
            <div className="w-50 flex flex-col">
              <div className="flex flex-row">
                <span>Type</span>
                <span className="text-litlingo-alert">*</span>
              </div>
              <SelectRedesign
                onChange={(value): void => handleSelectValue(value, 'type')}
                options={kindOptions}
                value={type?.toString() || ''}
                className={`h-9 ${
                  prodType !== type && compareMode ? 'bg-litlingo-focus-area-background' : ''
                }`}
                dropdownClassname="overflow-auto custom-scrollbar"
                disabled={shouldDisable}
              />
            </div>

            <div className="w-50 flex flex-col">
              <div className="flex flex-row">
                <span>Deploy with</span>
                <span className="text-litlingo-alert">*</span>
              </div>
              <SelectRedesign
                onChange={(value): void => handleSelectValue(value, 'model')}
                options={deploymentOptions}
                value={model?.toString() || ''}
                className={`h-9 ${
                  prodModel !== model && compareMode ? 'bg-litlingo-focus-area-background' : ''
                }`}
                dropdownClassname="overflow-auto custom-scrollbar"
                disabled={shouldDisable}
              />
            </div>

            <div className="w-50 flex flex-col">
              <div className="flex flex-row">
                <span>Source</span>
                <span className="text-litlingo-alert">*</span>
              </div>
              <SelectRedesign
                onChange={(value): void => handleSelectValue(value, 'content_source')}
                options={sourceOptions}
                value={source?.toString() || ''}
                className={`h-9 ${
                  prodSource !== source && compareMode ? 'bg-litlingo-focus-area-background' : ''
                }`}
                dropdownClassname="overflow-auto custom-scrollbar"
                disabled={shouldDisable}
              />
            </div>
          </div>

          <div className="w-78 flex flex-row gap-4">
            <TermsInput
              inputName="Categories"
              onChange={changeCategories}
              onDelete={deleteCategories}
              terms={categories}
              required={false}
            />
          </div>

          <div className="w-78 flex flex-row gap-4">
            <TermsInput
              inputName="Variables"
              onChange={changeVariables}
              onDelete={deleteVariables}
              terms={variables}
              required={false}
            />
          </div>

          <CategorizerWorkspaceTable />
        </div>
      </div>
    );
  };

  const getHeader = (): string => {
    if (selectedCustomer && customers?.find((c) => c.uuid === selectedCustomer)) {
      return customers.find((c) => c.uuid === selectedCustomer)?.name || '';
    }

    if (activeLanguageMatcher) return activeLanguageMatcher.name;

    return 'LLM Categorizer';
  };

  return (
    <>
      <CollapsableCard
        headerTitle={getHeader()}
        customAction={renderCustomActions()}
        containerClasses="h-full w-full border-none flex flex-col overflow-hidden"
        contentContainerClasses="flex h-full overflow-hidden"
        stickyHeader
      >
        {renderContent()}
      </CollapsableCard>
      <LeavePageModal
        title="Save changes?"
        text="Do you want to save your changes before moving on?"
        navigateButtontext="Cancel"
        cancelOrActionNavigateButtontext="Save"
        navigateAction={onSubmit}
        shouldBlockNavigation={unsaved}
        dicardAction={(): void => {
          dispatch(discardIdentifierChanges());
        }}
      />
      {isPromoteModalOpen && (
        <Modal
          title=" "
          body={renderBody()}
          okButton
          okButtonText="Push to Prod"
          okButtonOnClick={handlePromote}
          okButtonStyle="whitespace-no-wrap w-full px-4 mr-4"
          cancelButtonText="Go Back"
          cancelButtonOnclick={(): void => setIsPromoteModalOpen(false)}
          toggleShowModal={toggleModal}
        />
      )}
      {isMissingCategoriesModalOpen && (
        <Modal
          title=" "
          body={renderMissingCategories()}
          okButton
          okButtonText="Okay"
          okButtonOnClick={toggleCategoriesModal}
          okButtonStyle="whitespace-no-wrap w-42"
          cancelButton={false}
          toggleShowModal={toggleCategoriesModal}
        />
      )}
      {isMissingValuesModalOpen && (
        <Modal
          title=" "
          body={renderMissingValues()}
          okButton
          okButtonText="Okay"
          okButtonOnClick={toggleValuesModal}
          okButtonStyle="whitespace-no-wrap w-42"
          cancelButton={false}
          toggleShowModal={toggleValuesModal}
        />
      )}
    </>
  );
};

export default LLMCategorizer;
